import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { DownloadSimple, PenNib } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import Alert from '@/components/common/Alert/Alert';
import LoadingSpinner from '@/components/common/LoadingSpinner/LoadingSpinner';

GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

const API_BASE = import.meta.env.VITE_API_BASE_URL;

// Public page where a carrier signs our contract-comandă (c.dezshop.ro/semnare/:token).
// The PDF frozen when the link was made, a name, a finger signature and the consent;
// the backend seals it (dezshop-operations app/stoc/transport_orders/signing.py).
// Carriers are often foreign, so every label is Romanian / English.

/** The PDF's pages drawn on canvases as wide as the column. */
const PdfPages = ({ url }) => {
  const holder = useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const pdf = await getDocument({ url }).promise;
        const box = holder.current;
        if (!box || cancelled) return;
        box.innerHTML = '';
        const width = box.clientWidth || 600;
        const ratio = Math.min(window.devicePixelRatio || 1, 2);
        for (let n = 1; n <= pdf.numPages; n += 1) {
          const page = await pdf.getPage(n);
          if (cancelled) return;
          const viewport = page.getViewport({ scale: (width / page.getViewport({ scale: 1 }).width) * ratio });
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.style.width = '100%';
          canvas.className = 'mb-2 bg-white shadow-sm';
          box.appendChild(canvas);
          await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
        }
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => { cancelled = true; };
  }, [url]);

  if (failed) {
    return <p className="p-4 text-sm text-gray-600">Documentul nu a putut fi afișat. / The document could not be displayed. <a className="underline" href={url}>PDF</a></p>;
  }
  return <div ref={holder} className="min-h-[200px]"><div className="flex justify-center py-10"><LoadingSpinner /></div></div>;
};
PdfPages.propTypes = { url: PropTypes.string.isRequired };

/** A canvas to sign on with a finger, a pen or the mouse. */
const useSignaturePad = (canvasRef, enabled, onChange) => {
  const drawing = useRef(false);
  const last = useRef(null);

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    onChange(false);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!enabled || !canvas) return undefined;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    // Setting the canvas size wipes it. On a phone the window "resizes" when the
    // keyboard closes (tapping the consent box after typing the name) or the
    // address bar hides: the box keeps its size then, so nothing is touched; when it
    // really changes (the phone turned) the drawing is carried over, scaled.
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const width = Math.round(rect.width * ratio);
      const height = Math.round(rect.height * ratio);
      if (!width || !height || (canvas.width === width && canvas.height === height)) return;
      let previous = null;
      if (canvas.width && canvas.height) {
        previous = document.createElement('canvas');
        previous.width = canvas.width;
        previous.height = canvas.height;
        previous.getContext('2d').drawImage(canvas, 0, 0);
      }
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (previous) ctx.drawImage(previous, 0, 0, width, height);
      ctx.scale(ratio, ratio);
      ctx.lineWidth = 2.2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#111111';
    };
    const point = (e) => {
      const rect = canvas.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top, top: rect.top, left: rect.left };
    };
    // preventDefault keeps the focus where it is: a keyboard open stays open, so the
    // page does not jump under the finger
    const down = (e) => {
      e.preventDefault();
      canvas.setPointerCapture(e.pointerId);
      drawing.current = true;
      last.current = point(e);
    };
    const move = (e) => {
      if (!drawing.current) return;
      const p = point(e);
      // the page moved under the finger (the keyboard went away, the address bar):
      // the stroke goes on from here instead of drawing a line across the box
      if (Math.abs(p.top - last.current.top) > 1 || Math.abs(p.left - last.current.left) > 1) {
        last.current = p;
        return;
      }
      const ctx = canvas.getContext('2d');
      ctx.beginPath();
      ctx.moveTo(last.current.x, last.current.y);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
      last.current = p;
      onChange(true);
    };
    const up = () => { drawing.current = false; };
    resize();
    canvas.addEventListener('pointerdown', down);
    canvas.addEventListener('pointermove', move);
    canvas.addEventListener('pointerup', up);
    canvas.addEventListener('pointercancel', up);
    window.addEventListener('resize', resize);
    return () => {
      canvas.removeEventListener('pointerdown', down);
      canvas.removeEventListener('pointermove', move);
      canvas.removeEventListener('pointerup', up);
      canvas.removeEventListener('pointercancel', up);
      window.removeEventListener('resize', resize);
    };
  }, [canvasRef, enabled, onChange]);

  return clear;
};

const Notice = ({ title, body }) => (
  <div className="mx-auto max-w-lg px-4 py-16 text-center">
    <h1 className="text-xl font-semibold text-gray-800">{title}</h1>
    <p className="mt-2 text-sm text-gray-600">{body}</p>
  </div>
);
Notice.propTypes = { title: PropTypes.string.isRequired, body: PropTypes.string.isRequired };

const TransportOrderSigningPage = () => {
  const { token } = useParams();
  const base = `${API_BASE}/public/transport-orders/${encodeURIComponent(token)}`;
  const [data, setData] = useState(null);
  const [state, setState] = useState('loading'); // loading|ready|invalid|error
  const [name, setName] = useState('');
  const [consent, setConsent] = useState(false);
  const [hasStroke, setHasStroke] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(base);
        if (cancelled) return;
        if (res.status === 404) { setState('invalid'); return; }
        if (!res.ok) throw new Error(String(res.status));
        setData(await res.json());
        setState('ready');
      } catch {
        if (!cancelled) setState('error');
      }
    })();
    return () => { cancelled = true; };
  }, [base]);

  const signable = data?.status === 'valid';
  const clear = useSignaturePad(canvasRef, signable, setHasStroke);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${base}/sign`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ signature_png: canvasRef.current.toDataURL('image/png'), signer_name: name.trim(), consent: true }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || 'Semnarea a eșuat. / Signing failed.');
      setData(json);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (state === 'loading') {
    return <div className="flex min-h-screen items-center justify-center"><LoadingSpinner size="lg" /></div>;
  }
  if (state === 'invalid') return <Notice title="Link invalid" body="Linkul accesat nu există. Verificați mesajul primit de la noi. / This link does not exist." />;
  if (state === 'error') return <Notice title="Eroare / Error" body="Documentul nu a putut fi încărcat. Reîncercați. / Could not load the document. Please try again." />;
  if (data.status === 'revoked' || data.status === 'expired') {
    return <Notice title="Link expirat / Link expired" body="Acest link nu mai este valabil: comanda s-a modificat sau linkul a expirat. Cereți-ne unul nou. / This link is no longer valid; ask us for a new one." />;
  }

  const signed = data.status === 'signed';
  const title = `Contract-comandă ${data.doc_ref || ''}`.trim();

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <div className="mb-4">
        <h1 className="text-lg font-semibold text-gray-900">{title} <span className="font-normal text-gray-500">/ Transport order</span></h1>
        <p className="text-sm text-gray-600">
          {[data.issuer, data.carrier_name].filter(Boolean).join(' → ')}
        </p>
      </div>

      {signed && (
        <Alert type="success" className="mb-4">
          <Alert.Text className="flex flex-wrap items-center justify-between gap-3">
            <span className="font-semibold">Semnat de {data.signer_name}. Vă mulțumim! / Signed, thank you!</span>
            <a href={`${base}/pdf`} className="inline-flex items-center gap-2 rounded-md bg-brand-primary px-3 py-2 text-sm font-semibold text-white hover:bg-brand-primary-dark">
              <DownloadSimple size={16} weight="bold" /> Descarcă / Download
            </a>
          </Alert.Text>
        </Alert>
      )}

      <div className="lg:flex lg:items-start lg:gap-6">
        <div className="min-w-0 rounded-lg border bg-gray-50 p-2 lg:flex-1">
          {/* once signed the same address serves the signed PDF: the query makes it load again */}
          <PdfPages url={`${base}/pdf${signed ? '?signed=1' : ''}`} />
        </div>

        {signable && (
          <div className="mt-6 rounded-lg border bg-white p-4 lg:sticky lg:top-6 lg:mt-0 lg:w-[380px] lg:shrink-0">
            {/* the signature before the name: on a phone the keyboard is not open (and does not
                close, moving the page) while the finger draws */}
            <div className="mb-1 flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-800">Semnătura / Signature</span>
              <button type="button" onClick={clear} className="text-xs text-gray-600 underline">Șterge / Clear</button>
            </div>
            <canvas ref={canvasRef} className="mb-4 h-40 w-full touch-none rounded-md border-2 border-dashed border-gray-400 bg-white" />
            <label htmlFor="signer-name" className="mb-1 block text-sm font-semibold text-gray-800">
              Nume complet / Full name <span className="text-red-600">*</span>
            </label>
            <input id="signer-name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name"
              enterKeyHint="done" onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
              placeholder="Numele și prenumele / First and last name"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-primary focus:outline-none" />
            <label className="mt-4 flex cursor-pointer items-start gap-3">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-brand-primary" />
              <span className="text-xs text-gray-600">{data.consent_text}</span>
            </label>
            {error && <Alert type="error" compact className="mt-3"><Alert.Text>{error}</Alert.Text></Alert>}
            <Button className="mt-4 h-12 w-full bg-brand-primary text-base font-semibold text-white hover:bg-brand-primary-dark"
              onClick={submit} isLoading={submitting} disabled={name.trim().length < 2 || !consent || !hasStroke || submitting}>
              <PenNib size={20} /> Semnează / Sign
            </Button>
            <p className="mt-4 text-xs text-gray-500">
              Semnătură electronică conform Reg. (UE) 910/2014 (eIDAS) și Legii 214/2024. Se înregistrează data, ora, adresa IP și amprenta documentului.
              / Electronic signature under Reg. (EU) 910/2014 (eIDAS); the date, time, IP address and document fingerprint are recorded.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransportOrderSigningPage;
